/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { AllTransactionsType } from '../../../../../generated/graphql'
import { getCompanyNames, getInvExitsChartData, getQuarters } from './utils'
import Select from 'react-select'
import { selectCustomStyles } from '../../../../../utils/Select'
import { Bar } from 'react-chartjs-2'
import { GlobalContext } from '../../../store/context/globalContext'
import {
  formatNumber,
  getCurrency,
  getSymbolOfCurrency,
} from '../../../../../utils/CurrencyFormatter'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { KTSVG } from '../../../../../_metronic/helpers'
ChartJS.register(ArcElement, Tooltip, Legend)

type Props = {
  className: string
  allTransactions: AllTransactionsType[]
  counter: number
}

type FilterType = {
  label: string
  value: number
}

const ColumnChart: React.FC<Props> = ({ className, allTransactions, counter }) => {
  const { state } = useContext(GlobalContext)
  const { userCurrency } = state
  const userCurrencyFormat = getCurrency(userCurrency)
  const chartRef = useRef<ChartJS<'bar', number[], string>>(null)
  const [barCount, setBarCount] = useState(0)

  const [selectedCategory, setSelectedCategory] = useState<FilterType>({
    label: 'Company',
    value: 1,
  })

  // Memoize quarters and companies calculation
  const quarters = useMemo(() => getQuarters(allTransactions), [allTransactions])
  const companies = useMemo(() => getCompanyNames(allTransactions), [allTransactions])

  // Memoize chart data calculation with optimized dependencies
  const chartSeriesData = useMemo(() => {
    return getInvExitsChartData(allTransactions, quarters, companies)
  }, [allTransactions, quarters, companies])

  const { fundSeriesData, industrySeriesData, investmentSeriesData, companySeriesData } =
    chartSeriesData

  // Get current chart data based on selected category
  const currentChartData = useMemo(() => {
    switch (selectedCategory.label) {
      case 'Company':
        return companySeriesData
      case 'Investment':
        return investmentSeriesData
      case 'Industries':
        return industrySeriesData
      case 'Fund Vehicle':
        return fundSeriesData
      default:
        return companySeriesData
    }
  }, [
    selectedCategory.label,
    companySeriesData,
    investmentSeriesData,
    industrySeriesData,
    fundSeriesData,
  ])

  // Get current labels based on selected category
  const currentLabels = useMemo(() => {
    return selectedCategory.value === 1 ? companies : quarters
  }, [selectedCategory.value, companies, quarters])

  // Recalculate bar count when chart data or category changes
  useEffect(() => {
    if (currentChartData.length > 0) {
      const count = Math.max(...currentChartData.map((el) => el.data.length))
      setBarCount(count)
    } else if (currentLabels.length > 0) {
      // Fallback to labels count if chart data is empty
      setBarCount(currentLabels.length)
    }
  }, [currentChartData, currentLabels])

  // Initialize bar count on first load
  useEffect(() => {
    if (barCount === 0 && companySeriesData.length > 0) {
      const initialCount = Math.max(...companySeriesData.map((el) => el.data.length))
      setBarCount(initialCount)
    }
  }, [companySeriesData, barCount])

  const handleDropdownSelection = useCallback((e: any) => {
    setSelectedCategory(e)
  }, [])

  // Memoize filter options
  const categoryOptions: FilterType[] = useMemo(() => {
    const filtersList = ['Company', 'Investment', 'Industries', 'Fund Vehicle']
    return filtersList.map((filter: string, index: number) => ({
      value: index + 1,
      label: filter,
    }))
  }, [])

  // Memoize chart options with dynamic barThickness based on data count
  const options = useMemo(() => {
    // Use effective bar count for consistent behavior
    const effectiveBarCount =
      barCount > 0 ? barCount : currentLabels.length || companies.length || 1

    return {
      animation: false as const,
      plugins: {
        tooltip: {
          callbacks: {
            label: (ctx: any) =>
              isNaN(ctx?.raw)
                ? ''
                : ctx.dataset.label +
                  ': ' +
                  getSymbolOfCurrency(userCurrency) +
                  ' ' +
                  formatNumber({
                    value: ctx.raw,
                    format: userCurrencyFormat,
                  }),
          },
        },
        legend: {
          display: false,
        },
        title: {
          display: false,
          text: 'Total Invested over companies',
        },
      },
      borderRadius: 5,
      responsive: effectiveBarCount <= 6, // Only responsive for smaller datasets
      borderSkipped: 'bottom' as const,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 10,
          bottom: 10,
        },
      },
      interaction: {
        intersect: false,
      },
      barThickness:
        effectiveBarCount > 20
          ? 12
          : effectiveBarCount > 15
          ? 15
          : effectiveBarCount > 10
          ? 18
          : 20, // Dynamic bar thickness
      categoryPercentage: 0.8,
      barPercentage: 0.9,
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: effectiveBarCount > 10 ? 45 : 0, // Rotate labels for many items
            minRotation: 0,
            font: {
              size: effectiveBarCount > 20 ? 9 : effectiveBarCount > 15 ? 10 : 12, // Smaller font for many items
            },
            callback: function (value: string | number, index: number) {
              const chart = (this as any).chart
              const labels = chart?.data?.labels || []
              const label = labels[index] || ''
              const maxLength =
                effectiveBarCount > 20
                  ? 6
                  : effectiveBarCount > 15
                  ? 8
                  : effectiveBarCount > 10
                  ? 10
                  : 12
              return label.length > maxLength ? label.slice(0, maxLength) + '...' : label
            },
          },
        },
        y: {
          stacked: true,
          grid: {
            display: true,
          },
          ticks: {
            font: {
              size: 11,
            },
            callback: function (value: string | number) {
              return (
                getSymbolOfCurrency(userCurrency) +
                ' ' +
                formatNumber({
                  value: Number(value),
                  format: userCurrencyFormat,
                })
              )
            },
          },
        },
      },
    }
  }, [userCurrency, userCurrencyFormat, barCount, currentLabels.length, companies.length])

  // Optimize scroll functionality
  const scrollRight = useCallback((value: boolean) => {
    const container = document.getElementById('chart-container')
    const canvas = chartRef?.current?.ctx?.canvas
    if (container && canvas?.width) {
      const canvasWidth = canvas.width
      if (!value) {
        if (container.scrollLeft > 0) {
          container.scrollTo({
            left: container.scrollLeft - (container?.offsetWidth || 0),
            behavior: 'smooth',
          })
        }
      } else if (container.scrollLeft < canvasWidth) {
        container.scrollTo({
          left: container.scrollLeft + (container?.offsetWidth || 0),
          behavior: 'smooth',
        })
      }
    }
  }, [])

  // Calculate dynamic width based on data count and container
  const chartWidth = useMemo(() => {
    // Ensure we have a valid bar count
    const effectiveBarCount =
      barCount > 0 ? barCount : currentLabels.length || companies.length || 1

    if (effectiveBarCount <= 6) return 800
    if (effectiveBarCount <= 10) return 1000
    if (effectiveBarCount <= 15) return 1200
    return Math.min(1400 + (effectiveBarCount - 15) * 40, 2400) // Cap max width
  }, [barCount, currentLabels.length, companies.length])

  // Simplified chart generation without memoization issues
  const generateChart = () => {
    // Validate data before rendering
    if (
      !currentChartData ||
      currentChartData.length === 0 ||
      !currentLabels ||
      currentLabels.length === 0
    ) {
      return (
        <div className='d-flex align-items-center justify-content-center h-300px'>
          <p className='text-muted'>No data available for {selectedCategory.label}</p>
        </div>
      )
    }

    const plugins = [
      {
        id: 'resize-plugin',
        beforeDraw(chart: ChartJS) {
          // Force resize for smaller datasets
          const effectiveBarCount = barCount > 0 ? barCount : currentLabels.length
          if (effectiveBarCount < 6) {
            chart.resize()
          }
        },
      },
    ]

    return (
      <Bar
        key={selectedCategory.label}
        data={{
          datasets: currentChartData,
          labels: currentLabels,
        }}
        options={options}
        width={chartWidth}
        height={300}
        id='bar-chart'
        plugins={plugins}
        ref={chartRef}
      />
    )
  }

  useEffect(() => {
    // Only trigger scroll setup if we have chart data and sufficient items
    const effectiveBarCount = barCount > 0 ? barCount : currentLabels.length
    if (effectiveBarCount <= 6) return

    // Small delay to ensure chart is fully rendered before attempting scroll
    const timer = setTimeout(() => {
      const container = document.getElementById('chart-container')
      const canvas = chartRef?.current?.ctx?.canvas
      if (container && canvas?.width && canvas.width > container.offsetWidth) {
        // Scroll to show the latest data (rightmost)
        container.scrollTo({
          left: canvas.width - container.offsetWidth,
          behavior: 'smooth',
        })
      }
    }, 200) // Increased delay to ensure chart is fully ready

    return () => clearTimeout(timer)
  }, [selectedCategory, barCount, currentChartData, currentLabels.length]) // Trigger on category or data change

  return (
    <div
      className={`card ${className}`}
      style={{
        boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)',
        border: '1',
        borderColor: '#F1F1F2',
      }}
    >
      <span className='card-header p-0 border-0 px-9'>
        <h3 className='col-7 card-title align-items-start flex-column pt-6'>
          <span className='card-label fw-bold fs-3 mb-1'>Portfolio Tracking</span>

          <span className='text-muted fw-semibold fs-7'>{`By ${selectedCategory.label}`}</span>
        </h3>
        <div className='col-4 pt-9'>
          <Select
            className=''
            options={categoryOptions}
            onChange={(e: any) => handleDropdownSelection(e)}
            value={selectedCategory}
            styles={selectCustomStyles}
          />
        </div>
      </span>

      <div className={`card d-flex flex-row ${barCount > 6 || companies.length > 6 ? '' : 'px-9'}`}>
        {(barCount > 6 || companies.length > 6) && (
          <div
            className='menu-icon d-flex align-items-center mx-2 pb-10'
            data-kt-element='icon'
            onClick={() => {
              scrollRight(false)
            }}
          >
            <KTSVG
              path='/media/icons/duotune/arrows/arr022.svg'
              className='svg-icon-1 align-middle text-primary'
            />
          </div>
        )}
        <div
          className='card-body overflow-auto px-0'
          style={{
            overflowY: 'hidden',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            minHeight: '300px',
            maxHeight: '350px',
          }}
          id='chart-container'
        >
          {generateChart()}
        </div>
        {(barCount > 6 || companies.length > 6) && (
          <div
            className='menu-icon d-flex align-items-center mx-2 pb-10'
            data-kt-element='icon'
            onClick={() => {
              scrollRight(true)
            }}
          >
            <KTSVG
              path='/media/icons/duotune/arrows/arr023.svg'
              className='svg-icon-1 align-middle text-primary'
            />
          </div>
        )}
      </div>
    </div>
  )
}

export const MemoizedColumnChart = React.memo(ColumnChart)
